import merge from 'lodash/merge';
import find from 'lodash/find';
import remove from 'lodash/remove';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import mapValues from 'lodash/mapValues';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import transform from 'lodash/transform';
import isPlainObject from 'lodash/isPlainObject';
import { IEnvironmentOptions } from 'sharedtypes/rtx/scenario';

import { projectInterface } from '~/Scene/Tool/interface';
import { IStudioOptions } from '~/Scene/Viewer/interface';
import {
    _environment, _pipeline, _contentsManager, _camera, _pointofviewsManager, _system
} from '../Services/Studio/studioScene';
import { ContentAnimation } from '~/Scene/Content/contentAnimation';
import { JsonHelper, limitAccuracy } from '~/Scene/Tool/Json';
import { IPipelineOptions, defaultPipelineValues } from '~/Scene/Scenery/sceneryPipeline';
import { defaultEnvironmentValues } from '~/Scene/Scenery/sceneryEnvironment';

projectInterface.summary = {
    projectid: 'projectid',
    version: 'version',
    name: 'name',
    date: 'date',
    timestamp: 'timestamp',
};

const getDifference = (object, base) => {
    const Changes = (object, base) => transform(object, (result, value, key) => {
        if (!isEqual(value, base[key])) {
            if (isPlainObject(value) && isPlainObject(base[key])) {
                result[key] = Changes(value, base[key]);
            } else if (isArray(value) && isArray(base[key])) {
                result[key] = [];
                for (let i = 0; i < value.length; i++) {
                    // Can't do that as value[i] not always an object
                    // result[key][i] = Changes(value[i], base[key][i]);
                    // If not an object and not an array, keep the number or string value
                    if (
                        !isPlainObject(value[i])
                        && !isPlainObject(base[key][i])
                        && !isArray(value[i])
                        && !isArray(base[key][i])
                    ) {
                        result[key][i] = value[i];
                    } else if (!isEqual(value[i], base[key][i])) {
                        if (isPlainObject(value[i]) && isPlainObject(base[key][i])) {
                            result[key][i] = Changes(value[i], base[key][i]);
                        } else {
                            result[key][i] = value[i];
                        }
                    }
                }
            } else {
                result[key] = value;
            }
        }
    });
    return Changes(object, base);
};

export const LAST_VERSION = process.env.REACT_APP_VIEWER_VERSION;

export class ContextThreedJson extends JsonHelper {
    protected projectOptionKeys = [
        'name',
        'version',
        'versionManagement',
        'waterMark',
        'pushQuality',
        'websiteUrl',
        'offscreen',
        'fullScreen',
        'AR',
        'ARfirst',
        'configuration',
        'barVisible',
        'barPosition',
        'numberVisible',
        'titleVisible',
        'draggable',
        'playable',
        'dragAnimation',
        'cameraRotate',
        'imageUrl',
        'imageVisible',
    ];

    protected projectOptions: IStudioOptions = {};

    public setProjectOptions(project: IStudioOptions): void {
        // !Do not replace project or it will erase the id
        // this.project = project;
        const keys = Object.keys(project);
        keys.forEach((key) => {
            this.setProjectOption(key, project[key]);
        });
    }

    public setProjectOption(key: string, value): void {
        if (this.projectOptionKeys.indexOf(key) !== -1) this.projectOptions[key] = value;
    }

    public getProjectOptions(): IStudioOptions {
        return (this.projectOptions) ? cloneDeep(this.projectOptions) : {};
    }

    public getViewerVersion(): string {
        return this.projectOptions.version || LAST_VERSION;
    }

    public getAppJson(): IStudioOptions {
        const projectJson = this.getHarborJson();
        const pointofviewsJson = this.getAllPointofviewsJson();
        projectJson.pointofviews = pointofviewsJson;
        //! App will always use last version
        projectJson.version = LAST_VERSION;
        return projectJson;
    }

    public getAppRoundedJson(): IStudioOptions {
        return this.limitObjectAccuracy(this.getAppJson());
    }

    public getSceneJson(): IStudioOptions {
        const projectjson: IStudioOptions = {
            contents: this.getArrayContentsJson('mouse'),
            environment: this.getEnvironmentJson(_environment.currentValues),
            pipeline: this.getPipelineJson(_pipeline.currentValues),
            camera: this.objectToJson(projectInterface.camera, _camera.currentValues),
        };
        return projectjson;
    }

    private getPipelineJson(pipeline: IPipelineOptions) {
        const pipelineDiff = getDifference(pipeline, defaultPipelineValues);
        return this.objectToJson(projectInterface.pipeline, pipelineDiff);
    }

    private getEnvironmentJson(environment: IEnvironmentOptions) {
        const environmentDiff = getDifference(environment, defaultEnvironmentValues);
        return this.objectToJson(projectInterface.environment, environmentDiff);
    }

    private getPalettes() {
        const palettesJson: any = {};
        // palettesJson.assetimages = assetPicker.getSavedAssets();
        palettesJson.colors = cloneDeep(this.colors);
        const materials = _system.toolMaterial.getMaterialListToSave();
        const projectmaterials = [];
        materials.forEach((m) => {
            const mJson = this.recursiveObjectToJson(projectInterface.material, m);
            projectmaterials.push(mJson);
        });
        palettesJson.materials = projectmaterials;
        return palettesJson;
    }

    private colors: number[][] = []

    public setColors(colors: number[][] = []): void {
        this.colors = colors;
    }

    public addColor(color: number[]): number[][] {
        this.colors.push(color);
        return this.getColors();
    }

    public removeColor(color: number[]): number[][] {
        remove(this.colors, (c) => isEqual(c, color));
        return this.getColors();
    }

    public getColors(): number[][] {
        return cloneDeep(this.colors);
    }

    protected getHarborJson(): IStudioOptions {
        const sceneJson = this.getSceneJson();
        const projectOptions = this.getProjectOptions();
        //! Harbor will always use versionMangement if there is one
        //! Removed as engine changed
        // if (projectOptions.versionManagement) {
        //     projectOptions.version = projectOptions.versionManagement;
        // }
        const sceneAndSummary = merge(projectOptions, sceneJson);
        const materials = this.getPalettes();
        const sceneWithMaterialsJson = merge(sceneAndSummary, materials);
        return sceneWithMaterialsJson;
    }

    public getHarborRoundedJson(): IStudioOptions {
        return this.limitObjectAccuracy(this.getHarborJson());
    }

    private getAllPointofviewsJson() {
        const pointofviewsJson: any = {};
        _pointofviewsManager.pointofviews.forEach((pov) => {
            pointofviewsJson[pov.name] = this.getPointofviewJson(pov.name, true);
        });
        return pointofviewsJson;
    }

    private getPointofviewJson(pointofviewName: string, withEditorData: boolean) {
        const pov = find(_pointofviewsManager.pointofviews, (p) => p.name === pointofviewName);
        if (!withEditorData) {
            delete pov.name; // Name
        }
        return this.objectToJson(projectInterface.pointofview, pov);
    }

    private getArrayContentsJson(withAnimation?: 'mouse' | 'scroll') {
        const contentsJson: any = [];
        _contentsManager.list.forEach((content) => {
            if (content.type === 'model') content.updateDecalGeometries();
            if (content.visible) {
                contentsJson.push(this.getContentJson(content, false, withAnimation));
            }
        });
        return contentsJson;
    }

    private getContentJson(content: ContentAnimation, withEditorData: boolean, withAnimation?: 'mouse' | 'scroll') {
        const contentdata = this.objectToJson(projectInterface.content, content);
        const contenttypedata = this.objectToJson(projectInterface[content.type], content);

        if (!withEditorData) {
            // delete contentdata.i; // Id needed to build content object in JsonHelper
            // delete contentdata.v; // Visible needed
            // delete contentdata.pl; // Play needed
            delete contentdata.t; // Tag
            delete contentdata.sh; // Shadow
        }
        const contentmerged = merge(contentdata, contenttypedata);

        const newAnimations = {};
        const animKeys = Object.keys(content.animations);
        animKeys.forEach((animKey) => {
            const animation = content.animations[animKey];
            if (!withAnimation || (withAnimation && animation.event === withAnimation)) {
                newAnimations[animKey] = this.objectToJson(projectInterface.animation, animation);
            }
        });
        contentmerged.animations = newAnimations;
        return contentmerged;
    }

    private objectToJson(object, content: any) {
        return this.recursiveObjectToJson(object, content);
    }

    protected limitObjectAccuracy(object: IStudioOptions) {
        return this.mapValuesDeep(object, (a) => {
            if (typeof a === 'number') return limitAccuracy(a, 2);
            return a;
        });
    }

    private mapValuesDeep(v, callback: (v) => void) {
        if (isObject(v) && !isArray(v)) return mapValues(v, (v) => this.mapValuesDeep(v, callback));
        return callback(v);
    }
}
